import React from 'react';
import InlineToolCallIndicator from '../components/indicators/InlineToolCallIndicator';

const WORD_CHAR_REGEX = /[A-Za-z0-9_]/;

function isWordChar(char) {
  return WORD_CHAR_REGEX.test(char || '');
}

/**
 * Prevent tool pills from splitting words in half.
 * If a backend position lands inside a token, move it forward
 * to the token boundary so surrounding text remains readable.
 */
function getSafeInsertionPosition(text, rawPosition, minPosition = 0) {
  const safeText = text || '';
  const clamped = Math.max(0, Math.min(Number.isFinite(rawPosition) ? rawPosition : 0, safeText.length));
  let position = Math.max(clamped, minPosition);

  if (position > 0 && position < safeText.length) {
    const prevChar = safeText[position - 1];
    const nextChar = safeText[position];

    // If insertion lands mid-word, push to the end of the current token.
    if (isWordChar(prevChar) && isWordChar(nextChar)) {
      let cursor = position;
      while (cursor < safeText.length && isWordChar(safeText[cursor])) {
        cursor += 1;
      }
      position = cursor;
    }
  }

  return Math.max(position, minPosition);
}

/**
 * Resolves status text for a tool call from toolCallManager or message/toolCallPositions.
 * Backend sends detailed status (e.g. "Created contact in HubSpot", "Searched contacts (3 found)").
 */
function resolveToolStatus(toolCall, message) {
  // toolCallPositions from message have status as detailed string when complete (persisted from backend)
  if (toolCall.isComplete && typeof toolCall.status === 'string' && toolCall.status) {
    return toolCall.status;
  }
  // Backend persists user-facing status (e.g. "Created HTML chart") when tool completes
  if (typeof toolCall.status === 'string' && toolCall.status.trim() && toolCall.status !== 'executing') {
    return toolCall.status;
  }
  if (toolCall.status) {
    if (toolCall.status === 'executing') return toolCall.statusMessage || '';
    if (toolCall.status === 'success') return toolCall.statusMessage || '';
    if (toolCall.status === 'error') return toolCall.error || 'Failed';
  }

  const HUBSPOT_MCP_NAMES = ['search_crm_objects', 'get_crm_objects', 'get_properties', 'search_owners', 'search_properties', 'get_user_details'];
  const isHubSpotTool =
    toolCall.type === 'hubspot_function' ||
    toolCall.type === 'hubspot_mcp' ||
    toolCall.functionName?.includes('hubspot') ||
    HUBSPOT_MCP_NAMES.includes(toolCall.functionName);
  if (isHubSpotTool) {
    if (message?.isSearching || message?.searchStatus?.includes('Accessing') || message?.searchStatus?.includes('Searching')) {
      return '';
    }
    if (message?.hasHubSpotFunction) {
      const hubspotResults = message.hubspotFunctionResults || [];
      const specificResult = hubspotResults.find(r => r.functionName === toolCall.functionName);
      if (specificResult) {
        const fn = specificResult.functionName;
        const count = specificResult.recordCount || 0;
        if (fn?.includes('search')) {
          if (fn.includes('contact')) return count === 0 ? 'Searched contacts (0 found)' : count === 1 ? 'Searched contacts (1 found)' : `Searched contacts (${count} found)`;
          if (fn.includes('compan')) return count === 0 ? 'Searched companies (0 found)' : count === 1 ? 'Searched companies (1 found)' : `Searched companies (${count} found)`;
          if (fn.includes('deal')) return count === 0 ? 'Searched deals (0 found)' : count === 1 ? 'Searched deals (1 found)' : `Searched deals (${count} found)`;
          return count === 0 ? 'Searched HubSpot (0 found)' : `Searched HubSpot (${count} found)`;
        }
        if (fn?.includes('create')) {
          if (fn.includes('contact')) return count === 1 ? 'Created contact in HubSpot' : `Created ${count} contacts in HubSpot`;
          if (fn.includes('compan')) return count === 1 ? 'Created company in HubSpot' : `Created ${count} companies in HubSpot`;
          if (fn.includes('deal')) return count === 1 ? 'Created deal in HubSpot' : `Created ${count} deals in HubSpot`;
          return 'Created record in HubSpot';
        }
        if (fn?.includes('update')) {
          if (fn.includes('contact')) return count === 1 ? 'Updated contact in HubSpot' : `Updated ${count} contacts in HubSpot`;
          if (fn.includes('compan')) return count === 1 ? 'Updated company in HubSpot' : `Updated ${count} companies in HubSpot`;
          if (fn.includes('deal')) return count === 1 ? 'Updated deal in HubSpot' : `Updated ${count} deals in HubSpot`;
          return 'Updated record in HubSpot';
        }
        if (fn?.includes('delete')) {
          if (fn.includes('contact')) return count === 1 ? 'Deleted contact from HubSpot' : `Deleted ${count} contacts from HubSpot`;
          if (fn.includes('compan')) return count === 1 ? 'Deleted company from HubSpot' : `Deleted ${count} companies from HubSpot`;
          if (fn.includes('deal')) return count === 1 ? 'Deleted deal from HubSpot' : `Deleted ${count} deals from HubSpot`;
          return 'Deleted record from HubSpot';
        }
        return count === 1 ? 'Retrieved 1 record from HubSpot' : `Retrieved ${count} records from HubSpot`;
      }
    }
  }

  return '';
}

/**
 * Creates a tool indicator component - matches mobile pill design.
 */
function createToolIndicator(toolCall, message, darkMode, key, isFirst = false) {
  // Backend persists status as user-facing string (e.g. "Created HTML chart") when tool completes.
  // In-progress statuses like "Accessing HubSpot", "Checking your inbox..." must NOT be treated as complete.
  const hasPersistedCompletionStatus = typeof toolCall.status === 'string' && toolCall.status.trim() && toolCall.status !== 'executing';
  const inProgressStatusPattern = /^(Accessing|Checking|Working with|Executing|Sending via|Creating|Saving)/i;
  const isInProgressStatus = typeof toolCall.status === 'string' && inProgressStatusPattern.test(toolCall.status.trim());
  const isComplete =
    toolCall.isComplete === false || isInProgressStatus
      ? false
      : (toolCall.status === 'success' ||
         toolCall.status === 'error' ||
         toolCall.isComplete === true ||
         (hasPersistedCompletionStatus && !isInProgressStatus) ||
         (message?.isComplete && !message?.isStreaming));
  const success = !(toolCall.status === 'error' || toolCall.error || (toolCall.status && String(toolCall.status).toLowerCase().match(/error|failed/)));
  const status = resolveToolStatus(toolCall, message);
  const recordCount = toolCall.recordCount ?? message?.hubspotFunctionResults?.find(r => r.functionName === toolCall.functionName)?.recordCount;

  const isFileGen = toolCall.type === 'file_generation' ||
    ['create_html_artifact', 'generate_powerpoint_from_code', 'generate_html_chart'].includes(toolCall.functionName);
  return (
    <InlineToolCallIndicator
      key={key}
      isDark={darkMode}
      status={status}
      isComplete={isComplete}
      success={success}
      functionName={toolCall.functionName}
      recordCount={recordCount}
      isFirst={isFirst}
      codePreview={isFileGen && !isComplete ? message?._codePreview : undefined}
    />
  );
}

/**
 * Renders text with tool indicators inserted at specific positions
 */
export const renderTextWithInlineTools = (text, toolCallPositions = [], message = {}, darkMode = false) => {
  if (!text || !toolCallPositions || toolCallPositions.length === 0) {
    return text;
  }

  const sortedPositions = [...toolCallPositions].sort((a, b) => a.position - b.position);
  const segments = [];
  let lastPosition = 0;

  sortedPositions.forEach((toolCall, index) => {
    const position = getSafeInsertionPosition(text, toolCall.position, lastPosition);

    if (position > lastPosition) {
      const textSegment = text.slice(lastPosition, position);
      if (textSegment) segments.push(textSegment);
    }

    const toolKey = `tool-${toolCall.toolId || toolCall.functionName || index}-${position}`;
    segments.push(createToolIndicator(toolCall, message, darkMode, toolKey));
    lastPosition = position;
  });

  if (lastPosition < text.length) {
    const remainingText = text.slice(lastPosition);
    if (remainingText) segments.push(remainingText);
  }

  return segments.length === 1 ? segments[0] : segments;
};

/**
 * Processes markdown with inline tool indicators
 * @param {string} text - The message text  
 * @param {Array} toolCallPositions - Array of tool call position objects
 * @param {Object} message - The full message object for accessing status
 * @param {boolean} darkMode - Whether dark mode is active
 * @returns {Array} - Array of text segments and React elements
 */
export const processMarkdownWithInlineTools = (text, toolCallPositions = [], message = {}, darkMode = false) => {
  if (!toolCallPositions || toolCallPositions.length === 0) {
    return [text || ''];
  }
  const safeText = text || '';

  // Debug log during streaming
  // console.log('🔧 INLINE RENDERER: Processing text with tool positions', {
  //   messageId: message._id,
  //   isStreaming: message?.isStreaming,
  //   textLength: text.length,
  //   toolCallCount: toolCallPositions.length,
  //   toolCallPositions: toolCallPositions.map(p => ({
  //     position: p.position,
  //     functionName: p.functionName,
  //     toolId: p.toolId,
  //     type: p.type,
  //     status: p.status
  //   }))
  // });

  // Sort tool call positions by position (ascending for left-to-right processing)
  const sortedPositions = [...toolCallPositions].sort((a, b) => a.position - b.position);
  
  let result = [];
  let lastPosition = 0;
  
  sortedPositions.forEach((toolCall, index) => {
    const position = getSafeInsertionPosition(safeText, toolCall.position, lastPosition);
    
    // Add text before the tool call
    if (position > lastPosition) {
      const textSegment = safeText.slice(lastPosition, position);
      if (textSegment) {
        result.push(textSegment);
      }
    }
    
    // Add the tool indicator
    const toolKey = `tool-${toolCall.toolId || index}-${position}`;
    const toolIndicator = createToolIndicator(toolCall, message, darkMode, toolKey, result.length === 0);
    result.push(toolIndicator);
    
    lastPosition = position;
  });
  
  // Add remaining text after the last tool call
  if (lastPosition < safeText.length) {
    const remainingText = safeText.slice(lastPosition);
    if (remainingText) {
      result.push(remainingText);
    }
  }
  
  // console.log('🔧 INLINE RENDERER: Processed segments', {
  //   messageId: message._id,
  //   totalSegments: result.length,
  //   textSegments: result.filter(r => typeof r === 'string').length,
  //   toolSegments: result.filter(r => typeof r !== 'string').length
  // });
  
  return result;
}; 